import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Users, ChevronRight, Star, ArrowRight, Clock, Heart, Target, BookOpen, Eye, Compass } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

import { API } from "../config/api";

// Helper to normalize image URLs
const getImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) {
    if (url.includes(".preview.emergentagent.com/api/uploads/")) {
      const filename = url.split("/api/uploads/").pop();
      return `/api/uploads/${filename}`;
    }
    return url;
  }
  if (url.startsWith("/")) return url;
  return "/" + url;
};

// Icon mapping for dynamic rendering
const ICON_MAP = {
  Heart, Users, Calendar, Target, Eye, BookOpen, Compass, MapPin, Clock
};

// Stable static hero image - Diwali 2026 Flyer (local)
const STATIC_HERO_IMAGE = "/diwali-flyer-1.png";
// Fallback to a dark blue gradient background color if image fails to load
const FALLBACK_BG_COLOR = "#1a1a2e";

export default function HomePage() {
  const [pageData, setPageData] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [sponsorLogos, setSponsorLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroImageSrc, setHeroImageSrc] = useState(null);
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);
  const [heroImageError, setHeroImageError] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pageRes, faqsRes, testimonialsRes, logosRes] = await Promise.all([
        fetch(`${API}/site-pages/home`),
        fetch(`${API}/faqs`),
        fetch(`${API}/testimonials`),
        fetch(`${API}/sponsors/logos`),
      ]);

      if (pageRes.ok) {
        const data = await pageRes.json();
        if (!data.not_found) setPageData(data);
      }
      if (faqsRes.ok) setFaqs(await faqsRes.json());
      if (testimonialsRes.ok) setTestimonials(await testimonialsRes.json());
      if (logosRes.ok) setSponsorLogos(await logosRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to get section by type
  const getSection = (type) => {
    if (!pageData?.sections) return null;
    return pageData.sections.find(s => s.type === type);
  };

  // FORCE OVERRIDE: Always show Diwali 2026 on homepage
  const heroSection = {
    heading: "Diwali Kudumbasangamam 2026",
    subheading: "Celebrate Diwali with Tatvamasi",
    body: "November 7, 2026 • 5:00 PM",
    cta_text: "Book Tickets",
    cta_link: "/diwali-2026"
  };

  const aboutSection = getSection("about") || {
    heading: "About Tatvamasi",
    subheading: "OUR STORY",
    body: "Content coming soon..."
  };

  const meaningSection = getSection("meaning") || {
    heading: "The Meaning of Tatvamasi",
    body: "Content coming soon..."
  };

  const visionMissionSection = getSection("vision_mission") || { items: [] };
  const valuesSection = getSection("values") || { items: [] };
  const commitmentSection = getSection("commitment") || {};
  const milestonesSection = getSection("milestones") || { items: [] };
  
  // Filter out Vishu-related items from highlights
  const rawHighlights = getSection("highlights") || { items: [] };
  const highlightsSection = {
    ...rawHighlights,
    items: (rawHighlights.items || []).filter(item => 
      !item.title?.toLowerCase().includes('vishu')
    )
  };
  
  // FORCE OVERRIDE: Always show Diwali CTA
  const ctaSection = {
    heading: "Ready to Celebrate?",
    body: "Join us for an unforgettable Diwali celebration.",
    cta_text: "Book Your Tickets",
    cta_link: "/diwali-2026"
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="home-page">
      {/* Hero Section - Full Flyer Display */}
      <section 
        className="relative w-full" 
        style={{ backgroundColor: FALLBACK_BG_COLOR }}
        data-testid="hero-section"
      >
        {/* Background - Fallback color if image doesn't load */}
        <div className="relative w-full">
          {/* Hero Image - Full flyer display */}
          <Link to="/diwali-2026" className="block">
            <img
              src={STATIC_HERO_IMAGE}
              alt="Diwali Kudumbasangamam 2026 - Click to Book Tickets"
              className={`w-full h-auto max-h-[85vh] mx-auto object-contain transition-opacity duration-500 cursor-pointer ${heroImageLoaded ? 'opacity-100' : 'opacity-0'}`}
              onLoad={() => setHeroImageLoaded(true)}
              onError={() => {
                setHeroImageError(true);
                setHeroImageLoaded(true);
              }}
            />
          </Link>
          
          {/* Fallback content if image fails */}
          {heroImageError && (
            <div className="min-h-[60vh] flex flex-col items-center justify-center text-white text-center p-8">
              <h1 className="font-heading text-4xl md:text-6xl font-bold mb-4" style={{ color: '#D4AF37' }}>
                Diwali Kudumbasangamam 2026
              </h1>
              <p className="text-xl md:text-2xl mb-2">November 7, 2026 • 5:00 PM - 9:00 PM</p>
              <p className="text-lg">Ilderton Community Centre</p>
              <Link 
                to="/diwali-2026" 
                className="mt-6 btn-primary text-lg px-10 py-4 flex items-center justify-center gap-2"
              >
                <span>Book Tickets</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* CTA Buttons Section - Below Flyer */}
      <section className="py-6 bg-kerala-green" data-testid="cta-section">
        <div className="section-container">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link 
              to="/diwali-2026" 
              className="group btn-primary text-lg px-10 py-4 flex items-center justify-center gap-2 shadow-xl hover:scale-105 transition-transform"
            >
              <span>Book Tickets</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link 
              to="/sponsors" 
              className="btn-secondary text-lg px-8 py-4 flex items-center justify-center gap-2 border-2 border-kerala-gold text-kerala-gold hover:bg-kerala-gold hover:text-kerala-green transition-colors"
            >
              Become a Sponsor
            </Link>
          </div>
        </div>
      </section>

      {/* About Tatvamasi - Hero */}
      <section className="py-20 bg-kerala-green" data-testid="about-hero">
        <div className="section-container text-center">
          <p className="text-kerala-gold text-sm tracking-widest uppercase mb-4">{aboutSection.subheading}</p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-4">
            {aboutSection.heading}
          </h2>
          <p className="text-white/80 text-lg">Kerala Hindu's of London Ontario</p>
        </div>
      </section>

      {/* About Tatvamasi - Content */}
      <section className="section-padding bg-kerala-cream" data-testid="about-content">
        <div className="section-container max-w-4xl">
          <h3 className="font-heading text-3xl text-kerala-green mb-6">{aboutSection.heading}</h3>
          <p className="text-gray-600 leading-relaxed mb-8" dangerouslySetInnerHTML={{ __html: aboutSection.body?.replace(/Tatvamasi\/തത്വമസി/g, '<strong class="text-kerala-green">Tatvamasi/തത്വമസി</strong>') }} />

          {/* The Meaning of Tatvamasi */}
          <div className="bg-kerala-cream-alt border-l-4 border-kerala-gold p-6 rounded-r-lg">
            <h4 className="font-heading text-xl text-kerala-green mb-3">{meaningSection.heading}</h4>
            <p className="text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: meaningSection.body?.replace(/Tat Tvam Asi \(തത്വമസി\)/g, '<strong>Tat Tvam Asi (തത്വമസി)</strong>') }} />
          </div>
        </div>
      </section>

      {/* Vision, Mission, Philosophy */}
      {visionMissionSection.items?.length > 0 && (
        <section className="section-padding bg-kerala-cream-alt" data-testid="vision-mission">
          <div className="section-container">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {visionMissionSection.items.map((item, idx) => {
                const IconComponent = ICON_MAP[item.icon] || Eye;
                return (
                  <div key={idx} className="bg-white rounded-lg p-8 shadow-sm border border-kerala-gold/10">
                    <div className="w-12 h-12 bg-kerala-green/10 rounded-full flex items-center justify-center mb-4">
                      <IconComponent className="w-6 h-6 text-kerala-green" />
                    </div>
                    <h3 className="font-heading text-2xl text-kerala-green mb-4">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Our Values */}
      {valuesSection.items?.length > 0 && (
        <section className="py-20 bg-kerala-green" data-testid="our-values">
          <div className="section-container">
            <div className="text-center mb-16">
              <p className="text-kerala-gold text-sm tracking-widest uppercase mb-2">{valuesSection.subheading}</p>
              <h2 className="font-heading text-4xl md:text-5xl text-white">{valuesSection.heading}</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {valuesSection.items.map((value, idx) => {
                const IconComponent = ICON_MAP[value.icon] || Heart;
                return (
                  <div key={idx} className="text-center">
                    <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-8 h-8 text-kerala-gold" />
                    </div>
                    <h3 className="font-heading text-xl text-white mb-3">{value.title}</h3>
                    <p className="text-white/70 text-sm leading-relaxed">{value.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Our Commitment Quote */}
      {commitmentSection.heading && (
        <section className="py-24 bg-kerala-green" data-testid="commitment-quote">
          <div className="section-container max-w-4xl text-center">
            <p className="text-kerala-gold text-sm tracking-widest uppercase mb-6">OUR COMMITMENT</p>
            <blockquote className="font-heading text-3xl md:text-4xl lg:text-5xl text-white leading-tight mb-8">
              {commitmentSection.body}
            </blockquote>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              {commitmentSection.subheading}
            </p>
          </div>
        </section>
      )}

      {/* Milestones Timeline */}
      {milestonesSection.items?.length > 0 && (
        <section className="section-padding bg-kerala-cream" data-testid="milestones">
          <div className="section-container max-w-3xl">
            <div className="text-center mb-16">
              <p className="text-kerala-terracotta text-sm tracking-widest uppercase mb-2">{milestonesSection.subheading}</p>
              <h2 className="font-heading text-4xl md:text-5xl text-kerala-green">{milestonesSection.heading}</h2>
            </div>

            <div className="relative">
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-kerala-gold/30" />

              <div className="space-y-12">
                {milestonesSection.items.map((milestone, idx) => (
                  <div key={idx} className="relative flex gap-8">
                    <div className="relative z-10 w-16 h-16 bg-kerala-gold rounded-full flex items-center justify-center shrink-0">
                      <span className="text-white font-semibold text-sm">{milestone.year}</span>
                    </div>
                    <div className="pt-3">
                      <h3 className="font-heading text-xl text-kerala-green mb-2">{milestone.title}</h3>
                      <p className="text-gray-600">{milestone.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Highlights Section */}
      {highlightsSection.items?.length > 0 && (
        <section className="section-padding bg-kerala-cream-alt" data-testid="highlights-section">
          <div className="section-container">
            <div className="text-center mb-16">
              <p className="text-kerala-terracotta text-sm tracking-widest uppercase mb-2">{highlightsSection.subheading}</p>
              <h2 className="font-heading text-4xl md:text-5xl text-kerala-green">
                {highlightsSection.heading}
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {highlightsSection.items.map((item, idx) => (
                <div
                  key={idx}
                  className="card-premium p-8 text-center hover:-translate-y-2 transition-transform"
                >
                  <div className="text-5xl mb-4">{item.icon}</div>
                  <h3 className="font-heading text-xl text-kerala-green mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Event Details Banner */}
      <section className="py-12 bg-kerala-terracotta" data-testid="event-banner">
        <div className="section-container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex flex-col md:flex-row items-center gap-6 text-white">
              <div className="flex items-center gap-3">
                <Calendar className="w-6 h-6 text-white" />
                <span className="text-lg">November 7, 2026</span>
              </div>
              <div className="hidden md:block w-px h-8 bg-white/20" />
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6 text-white" />
                <span className="text-lg">5:00 PM - 9:00 PM</span>
              </div>
              <div className="hidden md:block w-px h-8 bg-white/20" />
              <div className="flex items-center gap-3">
                <MapPin className="w-6 h-6 text-white" />
                <span className="text-lg">Ilderton Community Centre</span>
              </div>
            </div>
            <Link to="/diwali-2026">
              <Button className="bg-white text-kerala-terracotta hover:bg-kerala-cream rounded-full px-8 py-4 font-medium">
                Get Your Tickets
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="section-padding bg-kerala-green" data-testid="testimonials-section">
          <div className="section-container">
            <div className="text-center mb-12">
              <p className="text-kerala-gold text-sm tracking-widest uppercase mb-2">Testimonials</p>
              <h2 className="font-heading text-4xl md:text-5xl text-white">
                What Our Community Says
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.slice(0, 3).map((testimonial, idx) => (
                <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-sm p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-kerala-gold text-kerala-gold" />
                    ))}
                  </div>
                  <p className="text-white/90 mb-6 italic">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    {testimonial.title && (
                      <p className="text-sm text-white/60">{testimonial.title}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sponsors Strip */}
      <section className="py-16 bg-kerala-cream border-y border-kerala-gold/20" data-testid="sponsors-strip">
        <div className="section-container">
          <div className="text-center mb-10">
            <p className="text-kerala-green/60 text-sm tracking-widest uppercase mb-2">Supported By</p>
            <h2 className="font-heading text-2xl text-kerala-green">Our Sponsors</h2>
          </div>

          {sponsorLogos.length > 0 ? (
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
              {sponsorLogos.slice(0, 6).map((logo, idx) => (
                <a
                  key={idx}
                  href={logo.website || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all"
                >
                  <img src={logo.url} alt={logo.name} className="h-12 md:h-16 object-contain" />
                </a>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap justify-center items-center gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-32 h-12 bg-kerala-green/5 rounded flex items-center justify-center text-kerala-green/30 text-sm"
                >
                  Sponsor {i}
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-10">
            <Link to="/sponsors">
              <Button className="btn-secondary" data-testid="become-sponsor-btn">
                Become a Sponsor
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <section className="section-padding bg-kerala-cream-alt" data-testid="faq-section">
          <div className="section-container max-w-3xl">
            <div className="text-center mb-12">
              <p className="text-kerala-terracotta text-sm tracking-widest uppercase mb-2">Questions?</p>
              <h2 className="font-heading text-4xl md:text-5xl text-kerala-green">
                Frequently Asked
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, idx) => (
                <AccordionItem 
                  key={idx} 
                  value={`faq-${idx}`}
                  className="bg-white border border-kerala-gold/20 rounded-sm px-6"
                >
                  <AccordionTrigger className="text-left font-heading text-lg text-kerala-green hover:text-kerala-terracotta">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-kerala-terracotta" data-testid="cta-section">
        <div className="section-container text-center">
          <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">
            {ctaSection.heading}
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            {ctaSection.body}
          </p>
          <Link to={ctaSection.cta_link || "/picnic-2026"}>
            <Button 
              className="bg-white text-kerala-terracotta hover:bg-kerala-cream px-10 py-6 text-lg rounded-full font-medium"
              data-testid="cta-book-tickets"
            >
              {ctaSection.cta_text || "Book Your Tickets"}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
